import { enablePointerEvents } from '@utils/pointer-events-control';
import GameButton from '@components/GameButton';
import BalanceScale from '@components/BalanceScale';
import TutorialOverlay from '@components/TutorialOverlay';
import { drawTitleScreen } from '@utils/title-screen';

/**
 * GameScene — Balance Out.
 *
 * Student drags blue weight-bags from the right shelf onto the right pan
 * of a balance to match the gray weight-block sitting on the left pan.
 *
 * Phase 1 implementation:
 *   - Real Figma-exported assets: wall, table, shelf, scale, weight bags + blocks
 *   - One sample question (target = 3 kg, bags 1..5 kg available)
 *   - Drag/drop with snap-to-pan / return-to-shelf
 *   - Confirm button validates sum
 *   - Confetti on correct
 *
 * Out of scope (waiting on script): scale tilt animation, voice lines,
 * question switching, tutorial flow, hint copy, end scene.
 */

const LAYOUT = {
  // (x, y) = pivot of the balance (the hole in the stand top). The
  // assembled BalanceScale extends ~125 px below the pivot (stand) and
  // ~120 px above it (rod + tray) at scale 0.7.
  scale: { pivotX: 430, pivotY: 380, scale: 1, startX: -500 },
  shelf: { rightX: 1280, bottomY: 720, startX: 1700 },
  // 6 dots matching the visual from the reference video. Question count
  // can exceed dots — extra completions just skip the visual marker.
  progress: { x: 380, y: 60, startY: -70, dots: 6 },
};

// 4-bag layout: each question shows up to 4 weight bags on the shelf
// arranged in 2x2. Positions measured from the video frame.
const BAG_POSITIONS = [
  { x: 1013, y: 153 }, // top-left
  { x: 1178, y: 148 }, // top-right
  { x: 1009, y: 315 }, // mid-left
  { x: 1178, y: 310 }, // mid-right
];

// Question bank. Index 0 is the tutorial. Each entry lists the target
// weight (on the left pan) and the 4 weight bags shown on the right
// shelf. Students must combine bags so their sum equals the target.
const QUESTIONS = [
  { targetKg: 4, bags: [1, 2, 3, 5] }, // idx 0 — tutorial (Q2 in the curriculum)
  { targetKg: 3, bags: [1, 2, 4, 6] }, // idx 1 — Q1
  { targetKg: 5, bags: [2, 3, 4, 7] }, // idx 2 — Q3
  { targetKg: 5, bags: [1, 3, 4, 6] }, // idx 3 — Q4
  { targetKg: 6, bags: [2, 3, 4, 5] }, // idx 4 — Q5
  { targetKg: 7, bags: [2, 3, 4, 5] }, // idx 5 — Q6 → EndScene
];

function bagHomesFor(bagKgs) {
  const homes = {};
  bagKgs.forEach((kg, i) => {
    if (i < BAG_POSITIONS.length) homes[kg] = BAG_POSITIONS[i];
  });
  return homes;
}

// Bag style per question. The "sack" style reuses the existing
// kg-labelled `weight-bag-Xkg` PNGs (the familiar blue bag from the
// start screen); all other styles use neutral PNGs and the kg label
// is painted on at runtime via `bakeBagTexture()` so one artwork can
// serve every kg value.
//
// Tutorial (idx 0) keeps the blue sack so the visual matches the
// start-screen handoff. From Q1 onwards we cycle through the five
// distinct styles so the student never sees the same shelf twice in
// a row.
//   idx 0 → tutorial → sack
//   idx 1 → Q1       → card
//   idx 2 → Q3       → jar
//   idx 3 → Q4       → carton
//   idx 4 → Q5       → box
//   idx 5 → Q6       → ticket
//   idx ≥6           → cycle restarts at "card"
const POST_TUTORIAL_STYLES = ['card', 'jar', 'carton', 'box', 'ticket'];

// Per-style render config. `baseKey` is the loaded texture; if not
// `preBaked` then the kg label is painted on top at runtime. `label`
// drives the painted text (color/font/offset).
const BAG_STYLES = {
  sack: {
    baseKey: 'weight-bag', // suffix-per-kg, e.g. weight-bag-1kg
    preBaked: true,
  },
  card: {
    baseKey: 'bag-card-purple',
    label: { color: '#5a1075', offsetY: 14, fontSize: 28 },
  },
  jar: {
    baseKey: 'bag-jar-yellow',
    label: { color: '#3d2200', offsetY: 22, fontSize: 26 },
  },
  carton: {
    baseKey: 'bag-carton-green',
    label: { color: '#0f5b2b', offsetY: 14, fontSize: 28 },
  },
  box: {
    baseKey: 'bag-box-yellow',
    label: { color: '#3d2200', offsetY: 6, fontSize: 24 },
  },
  ticket: {
    baseKey: 'bag-ticket-purple',
    label: { color: '#5a1075', offsetY: 8, fontSize: 28 },
  },
};

function bagStyleFor(idx) {
  if (idx === 0) return 'sack';
  return POST_TUTORIAL_STYLES[(idx - 1) % POST_TUTORIAL_STYLES.length];
}

// Right pan can hold up to 3 bags. Slot 0 is centred over the pan,
// slot 1 sits to the left of slot 0, and slot 2 sits to the right —
// so the third bag flanks the first instead of stacking further left.
const MAX_BAGS_ON_PAN = 3;
const STACK_STRIDE_X = 70;
const STACK_STRIDE_Y = 18;
function stackOffset(idx) {
  if (idx === 0) return { dx: 0, dy: 0 };
  if (idx === 1) return { dx: -STACK_STRIDE_X, dy: STACK_STRIDE_Y };
  if (idx === 2) return { dx: STACK_STRIDE_X, dy: STACK_STRIDE_Y };
  return { dx: -idx * STACK_STRIDE_X, dy: idx * STACK_STRIDE_Y };
}

/**
 * Per-kg display scale for a draggable bag. The blue "sack" style
 * uses kg-specific PNGs that already grow with weight, so its scale
 * Every style is now pre-baked at the artist-intended size, so no
 * runtime scaling is applied — the imported PNG drives the visual
 * size by itself.
 */
function bagTargetScale() {
  return 1.0;
}

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
  }

  init() {
    // End-of-game stats — kept as scene-instance fields and forwarded
    // to EndScene on launch.
    this.score = 0; // questions answered correctly
    this.attempted = 0; // questions completed (correct or skipped)
    this.totalHints = 0; // count of hint-modal opens across the game
    this.totalAttempts = 0; // count of weigh-button presses (correct + wrong)
  }

  preload() {}

  create() {
    // Enable pointer events so hover states and the cursor work
    enablePointerEvents();

    this.createAnimations();

    this.placedBags = [];

    // 1. Mirror StartScene's layout pixel-for-pixel so the scene-switch is
    //    a seamless handoff (no flash, same items at same positions).
    //    `rightShelf` is owned by drawTitleScreen but reused below for the
    //    entrance slide-in.
    this.titleItems = drawTitleScreen(this);
    this.rightShelfSprite = this.titleItems.rightShelf;

    // Static decoration: a non-interactive copy of the Play button so we
    // can fade it out as part of the slide-off (the interactive one lived
    // in StartScene and is already gone).
    this.fakePlayButton = this.add
      .image(529 + 229 / 2, 334 + 64 / 2, 'playButton')
      .setDepth(5);

    // 2. Pre-stage the gameplay elements off-screen / hidden — they
    //    enter once the slide-off has finished.
    this.drawScale();
    this.drawProgress();
    // Start at question 0 (the tutorial).
    this.questionIndex = 0;
    const q0 = QUESTIONS[this.questionIndex];
    this.targetKg = q0.targetKg;
    this.currentBagHomes = bagHomesFor(q0.bags);
    this.drawTargetBlock(q0.targetKg);
    this.spawnDraggableBags(q0.bags);
    this.drawConfirmButton();

    this.scene.launch('UIScene');

    // 3. Slide-off → entrance → gameplay enabled.
    this.playSlideOffAnimation(() => {
      this.repositionForEntrance();
      this.playEntranceAnimation(() => this.enableInteractions());
    });
  }

  update() {
    // Glue stuck bags to the right-pan world position so they follow
    // the scale even if any container-transform refresh lags behind.
    if (!this.balanceScale || !this.placedBags || this.placedBags.length === 0) return;
    const pan = this.balanceScale.getRightTrayPos();
    this.placedBags.forEach((bag, idx) => {
      if (!bag.getData('stuck')) return;
      const { dx, dy } = stackOffset(idx);
      if (bag.parentContainer === this.balanceScale.rightTray) {
        bag.x = dx;
        bag.y = this.balanceScale.rightPanSurfaceY + dy;
      } else {
        bag.x = pan.x + dx;
        bag.y = pan.y + dy;
      }
    });
  }

  // ==================== Animations ====================

  createAnimations() {
    const make = (key, atlas, prefix, end, frameRate = 30, repeat = 0) => {
      if (this.anims.exists(key)) return;
      this.anims.create({
        key,
        frames: this.anims.generateFrameNames(atlas, {
          start: 0,
          end,
          prefix,
          suffix: '.png',
        }),
        frameRate,
        repeat,
      });
    };
    make('balanceConfetti', 'balanceConfetti', 'CONFETTI', 89, 60, 0);
    make('bluePacket', 'bluePacketAnim', 'BLUEPACKET', 29, 30, 0);
    make('carryBagBack', 'carryBagBackAnim', 'CARRYBAGBACK', 129, 60, 0);
    make('carryBagFront', 'carryBagFrontAnim', 'CARRYBAGFRONT', 129, 60, 0);

    // Lightbulb hint-icon animations (atlas frames seq_0_0.png .. 66).
    // Pattern lifted from cardistry: pop in → breathe loop while idle,
    // switch to idle once clicked, pop_out on close. Breathe/idle start
    // at frame 36 (steady-state) so they can't reuse the make() helper.
    const hintFrames = (start, end) =>
      this.anims.generateFrameNames('hintFinalAnim', {
        start,
        end,
        prefix: 'seq_0_',
        suffix: '.png',
      });
    if (!this.anims.exists('hintPop')) {
      this.anims.create({
        key: 'hintPop',
        frames: hintFrames(0, 35),
        frameRate: 30,
        repeat: 0,
      });
    }
    if (!this.anims.exists('hintBreathe')) {
      this.anims.create({
        key: 'hintBreathe',
        frames: hintFrames(36, 66),
        frameRate: 30,
        repeat: -1,
      });
    }
    if (!this.anims.exists('hintIdle')) {
      this.anims.create({
        key: 'hintIdle',
        frames: hintFrames(36, 37),
        frameRate: 30,
        repeat: -1,
      });
    }
    if (!this.anims.exists('hintPopOut')) {
      this.anims.create({
        key: 'hintPopOut',
        frames: hintFrames(35, 0),
        frameRate: 30,
        repeat: 0,
      });
    }
  }

  playConfetti() {
    const confetti = this.add.sprite(640, 360, 'balanceConfetti').setDepth(30);
    confetti.play('balanceConfetti');
    confetti.once('animationcomplete', () => confetti.destroy());
  }

  // ==================== Slide-off (intro) animation ====================

  /**
   * First half of `correct answer_3.mp4` (~0–3.3s): the title and Play
   * button fade, the foreground props + weight blocks slide off-screen
   * sideways with a small downward arc, and the shelves + shelf-items
   * fade out so the room is empty by the time the entrance starts.
   */
  playSlideOffAnimation(onComplete) {
    const t = this.titleItems;

    // Fade title artwork + (decorative) Play button
    this.tweens.add({
      targets: [t.gameName, this.fakePlayButton],
      alpha: 0,
      duration: 200,
      ease: 'Sine.easeOut',
    });

    // Per-item slide. dx sign = direction; delay/duration/arc tuned
    // against the video frames so motion is uneven rather than uniform.
    // Front-most layers slide off FIRST, back layers FOLLOW with a
    // stepped delay (depth-4 weights → depth-3 blueRight → depth-2 props
    // → depth-1 blueLeft).
    const slides = [
      // Tier 1 — depth 4 (weight blocks, front-most)
      { s: t.weight2kg, dx: -800, delay: 0, dur: 900, arc: 14 },
      { s: t.weight1kg, dx: 800, delay: 0, dur: 900, arc: 18 },
      { s: t.weight3kg, dx: 800, delay: 40, dur: 900, arc: 16 },
      { s: t.weight4kg, dx: 800, delay: 80, dur: 900, arc: 14 },

      // Tier 2 — depth 3 (itemBlueRight)
      { s: t.itemBlueRight, dx: 800, delay: 150, dur: 1000, arc: 18 },

      // Tier 3 — depth 2 (mid-layer props)
      { s: t.itemPink, dx: -800, delay: 280, dur: 1100, arc: 18 },
      { s: t.itemYellowLeft, dx: -800, delay: 280, dur: 1100, arc: 22 },
      { s: t.itemGreen, dx: 800, delay: 320, dur: 1100, arc: 26 },
      { s: t.itemYellowRight, dx: 800, delay: 360, dur: 1100, arc: 24 },

      // Tier 4 — depth 1 (back-most, itemBlueLeft)
      { s: t.itemBlueLeft, dx: -800, delay: 480, dur: 1200, arc: 26 },
    ];

    let latest = 0;
    slides.forEach(({ s, dx, delay, dur, arc }) => {
      this.tweens.add({
        targets: s,
        x: { value: `${dx > 0 ? '+' : '-'}=${Math.abs(dx)}`, ease: 'Sine.easeIn' },
        y: { value: `+=${arc}`, duration: dur, ease: 'Sine.easeInOut' },
        duration: dur,
        delay,
      });
      latest = Math.max(latest, delay + dur);
    });

    // Shelves slide off LAST, after every prop has left — they're the
    // back-most layer of the title screen. Start ~250 ms after the
    // back-most prop begins moving so the order reads clearly.
    const shelfDelay = latest - 250;
    const shelfDur = 1000;
    this.tweens.add({
      targets: t.leftShelf,
      x: '-=340',
      duration: shelfDur,
      delay: shelfDelay,
      ease: 'Sine.easeIn',
    });
    this.tweens.add({
      targets: t.rightShelf,
      x: '+=340',
      duration: shelfDur,
      delay: shelfDelay,
      ease: 'Sine.easeIn',
    });

    const shelfEnd = shelfDelay + shelfDur;
    this.time.delayedCall(Math.max(latest, shelfEnd) + 80, onComplete);
  }

  /**
   * Cleans up the slid-off title-screen items and pre-stages the right
   * shelf at its off-screen-right starting position for the entrance
   * slide-in.
   */
  repositionForEntrance() {
    const t = this.titleItems;
    [
      t.leftShelf,
      t.itemPink,
      t.itemBlueLeft,
      t.itemYellowLeft,
      t.itemYellowRight,
      t.itemGreen,
      t.itemBlueRight,
      t.weight1kg,
      t.weight2kg,
      t.weight3kg,
      t.weight4kg,
      t.gameName,
      this.fakePlayButton,
    ].forEach(s => s.destroy());

    // Right shelf: swap to the tutorial variant (`right-shelf-tutorial`
    // — open compartments sized for the tutorial step), restore alpha,
    // and park it off-screen-right so the entrance can slide it back
    // in.
    this.rightShelfSprite
      .setTexture('right-shelf-tutorial')
      .setAlpha(1)
      .setX(LAYOUT.shelf.startX);
  }

  // ==================== Entrance animation ====================

  /**
   * Reproduces the post-slide-off sequence in `correct answer_3.mp4`
   * (~3.4s–6s): scale slides in from off-screen-left, right shelf slides
   * in from off-screen-right, and the progress-bar rectangle drops from
   * above — all in parallel with a smooth Sine.easeOut (no overshoot,
   * matches the video). Once the rectangle lands, the 6 dots pop in.
   * Then the target weight drops onto the left pan, and finally the
   * weight bags fade onto the shelf in a staggered cascade. Drag input
   * is held off until the cascade finishes.
   */
  playEntranceAnimation(onComplete) {
    const startDelay = 200;
    const slideDur = 450;

    // Phase 1 — scale + right shelf + progress rect slide/drop in (parallel)
    this.tweens.add({
      targets: this.balanceScale,
      x: LAYOUT.scale.pivotX,
      duration: slideDur,
      delay: startDelay,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: this.rightShelfSprite,
      x: LAYOUT.shelf.rightX,
      duration: slideDur,
      delay: startDelay,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: this.progressBar,
      y: LAYOUT.progress.y,
      duration: slideDur,
      delay: startDelay + 40,
      ease: 'Sine.easeOut',
      onUpdate: () => this.redrawProgressCords(),
    });

    // Phase 2 — dots fade-pop in left-to-right after the rect lands
    const dotsStart = startDelay + slideDur + 40;
    const dotStep = 50;
    this.progressDots.forEach((dot, i) => {
      this.tweens.add({
        targets: dot,
        alpha: 1,
        scale: 1,
        duration: 200,
        delay: dotsStart + i * dotStep,
        ease: 'Sine.easeOut',
      });
    });

    // Phase 3 — target block drops onto the left pan. The scale is
    // off-screen until ~startDelay+slideDur, so we defer reading the
    // left-pan world position until the drop actually starts.
    const targetDelay = dotsStart + this.progressDots.length * dotStep + 150;
    this.time.delayedCall(targetDelay, () => {
      const leftPanWorld = this.balanceScale.getLeftTrayPos();
      this.targetSprite.x = leftPanWorld.x;
      this.targetSprite.setAlpha(1);
      this.tweens.add({
        targets: this.targetSprite,
        y: leftPanWorld.y,
        duration: 480,
        ease: 'Quad.easeIn',
        onComplete: () => {
          this.attachToLeftPan(this.targetSprite);
          // Now tilt the scale (left-heavy by the target weight).
          this.balanceScale.setWeights(this.targetKg, 0);
        },
      });
    });

    // Phase 4 — weight bags fade onto shelf, in pairs by row
    //   pair 1: 1kg + 2kg     pair 2: 3kg + 4kg     solo: 5kg
    const bagStart = targetDelay + 550;
    const rowStep = 320;
    const rowOf = kg => (kg <= 2 ? 0 : kg <= 4 ? 1 : 2);
    this.draggables.forEach(bag => {
      this.tweens.add({
        targets: bag,
        alpha: 1,
        scale: bag.getData('targetScale') ?? 1,
        duration: 260,
        delay: bagStart + rowOf(bag.getData('kg')) * rowStep,
        ease: 'Sine.easeOut',
      });
    });

    // Phase 5 — drag-hint: each bag spawns a ghost copy slightly to the
    // right and behind, suggesting the bag is a draggable stack.
    const lastBagEnd = bagStart + 2 * rowStep + 260;
    const ghostStart = lastBagEnd + 200;
    this.spawnBagGhosts(ghostStart);

    const finishAt = ghostStart + 450 + 80;
    this.time.delayedCall(finishAt, onComplete);
  }

  /**
   * Spawns a second, real draggable bag behind each original — the
   * "ghost" reads as a peek-out duplicate that hints the bag is a
   * stack, and it functions as a full sibling: drag, snap to pan,
   * return home, count toward the answer sum.
   *
   * The slide-in (start at the original bag's x, ease 16 px to the
   * right) matches the entrance hint. Drag is held off until the
   * slide completes so the bag can't be grabbed mid-flight.
   */
  spawnBagGhosts(delay) {
    if (!this.bagGhosts) this.bagGhosts = [];
    const ghostSlideX = 16;
    const style = bagStyleFor(this.questionIndex);

    // Snapshot before push so the newly-created ghosts don't get
    // ghosted themselves on this pass.
    const originals = this.draggables.filter(b => !b.getData('isGhost'));

    originals.forEach(bag => {
      if (bag.getData('hasGhost')) return;

      const kg = bag.getData('kg');
      // Read from the bag's stored shelf position rather than its
      // current x/y — the original may already have been dragged or
      // reparented onto the pan by the time this runs.
      const bagHome = bag.getData('home');
      if (!bagHome) return;
      const ghostHome = { x: bagHome.x + ghostSlideX, y: bagHome.y };
      const ghost = this.makeDraggableBag(kg, ghostHome, style);

      // Override makeDraggableBag's pre-fade-in state: the ghost sits
      // fully-rendered behind the original at the original's spot,
      // hidden until the slide begins.
      ghost.setAlpha(1);
      ghost.setScale(ghost.getData('targetScale') ?? 1);
      ghost.x = bagHome.x;
      ghost.y = bagHome.y;
      ghost.setDepth(bag.depth - 1);
      ghost.setVisible(false);
      ghost.setData('isGhost', true);
      // Keep the original at the top of the display list so hit-tests
      // prefer it when the bounding boxes overlap.
      this.children.bringToTop(bag);

      this.tweens.add({
        targets: ghost,
        x: ghostHome.x,
        duration: 400,
        delay,
        ease: 'Sine.easeOut',
        onStart: () => ghost.setVisible(true),
        onComplete: () => {
          if (!ghost.scene) return;
          ghost.setInteractive({ cursor: 'grab' });
          this.input.setDraggable(ghost);
        },
      });

      bag.setData('hasGhost', true);
      this.draggables.push(ghost);
      this.bagGhosts.push(ghost);
    });
  }

  enableInteractions() {
    this.runTutorial();
  }

  // ==================== Tutorial ====================

  /**
   * Walks the student through the first question:
   *   1. Spotlight the target block on the left pan
   *   2. Spotlight the bag shelf
   *   3. Spotlight the matching (4 kg) bag with a drag hint
   *   4. Wait for the bag to land on the right pan
   *   5. Closing message + hand off to free-play
   * Steps 1–3 advance on click; step 4 advances when the drag finishes.
   */
  runTutorial() {
    this.tutorial = new TutorialOverlay(this);
    this.tutorial.show();
    this.tutorialStep = 0;
    this.input.on('pointerdown', this.onTutorialAdvance, this);
    this.advanceTutorial();
  }

  advanceTutorial() {
    const t = this.tutorial;
    t.clearSpotlight();
    t.hideTooltip();
    if (this.tutorialHand) {
      this.tutorialHand.destroy();
      this.tutorialHand = null;
    }
    this.tutorialStep += 1;

    if (this.tutorialStep === 1) {
      // Step 1 — the target block.
      t.spotlight([this.targetSprite]);
      const pos = this.balanceScale.getLeftTrayPos();
      t.showTooltip(`Here's a ${this.targetKg} kg weight block.`, {
        x: pos.x + 300,
        y: pos.y - 20,
        arrow: 'left',
      });
    } else if (this.tutorialStep === 2) {
      // Step 2 — spotlight the scale (with target on left pan) and the
      // bags on the shelf. All bags drag normally; the tutorial just
      // gates the advance on the 3 kg bag landing on the right pan.
      this.input.off('pointerdown', this.onTutorialAdvance, this);

      const threeKgBag = this.draggables.find(b => b.getData('kg') === 3);
      this.tutorialHand = this.add
        .image(threeKgBag.x + 40, threeKgBag.y + 50, 'handCursor')
        .setOrigin(0.26, 0.19)
        .setDepth(t.SPOTLIGHT_DEPTH);
      this.tweens.add({
        targets: this.tutorialHand,
        scale: 0.9,
        duration: 450,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });

      // Don't spotlight already-placed bags — they live inside
      // balanceScale's rightTray and will render with it. Pulling them
      // out (which spotlight does for container children) detaches them
      // from the pan and breaks subsequent attaches. Ghosts are now
      // part of `this.draggables`, so the same filter covers them.
      t.spotlight([
        this.balanceScale,
        ...this.draggables.filter(b => !b.getData('onPan')),
      ]);
      t.showTooltip(
        'Combine the packets from the shelf and place it on the scale to balance it',
        { x: 640, y: 615, arrow: 'none' },
      );

      this.enableAllBagsForDrag();
      this.watchForBagOnPan(3, () => this.advanceTutorial());
    } else if (this.tutorialStep === 3) {
      // Step 3 — student needs a second packet to make the right pan
      // equal the 4 kg target. All bags stay draggable; we just point
      // the hand at the 1 kg bag and advance when it lands on the pan.
      this.input.off('pointerdown', this.onTutorialAdvance, this);

      const oneKgBag = this.draggables.find(b => b.getData('kg') === 1);
      this.tutorialHand = this.add
        .image(oneKgBag.x + 40, oneKgBag.y + 50, 'handCursor')
        .setOrigin(0.26, 0.19)
        .setDepth(t.SPOTLIGHT_DEPTH);
      this.tweens.add({
        targets: this.tutorialHand,
        scale: 0.9,
        duration: 450,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });

      // Don't spotlight already-placed bags — they live inside
      // balanceScale's rightTray and will render with it. Pulling them
      // out (which spotlight does for container children) detaches them
      // from the pan and breaks subsequent attaches. Ghosts are now
      // part of `this.draggables`, so the same filter covers them.
      t.spotlight([
        this.balanceScale,
        ...this.draggables.filter(b => !b.getData('onPan')),
      ]);
      t.showTooltip('Add another packet to make it equal', {
        x: 640,
        y: 615,
        arrow: 'none',
      });

      this.enableAllBagsForDrag();
      this.watchForBagOnPan(1, () => this.advanceTutorial());
    } else if (this.tutorialStep === 4) {
      // Step 4 — show "Let's Weigh!" button with a tooltip arrow
      // pointing to it. Pressing the button ends the tutorial.
      this.input.off('pointerdown', this.onTutorialAdvance, this);

      // Same scale-up-on-hover / scale-down-on-press as the Play button,
      // no hover texture.
      // Same press-feel as the title-screen Play button.
      // Same press-feel as the title-screen Play button — scale up on
      // hover, down on press. On click (pointerup) the button vanishes
      // immediately so the payoff plays without it in frame.
      this.weighBtn = new GameButton(this, 640, 650, 'letsWeigh', {
        hoverScale: 1.0,
        pressScale: 0.92,
        tweenDuration: 120,
      })
        .onClick(() => {
          // Tween back to scale 1.0 from the pressScale, then instantly
          // vanish and run the payoff.
          this.tweens.killTweensOf(this.weighBtn);
          this.tweens.add({
            targets: this.weighBtn,
            scaleX: 1,
            scaleY: 1,
            duration: 120,
            ease: 'Sine.easeOut',
            onComplete: () => {
              this.weighBtn.setVisible(false);
              this.playWeighReveal();
            },
          });
        })
        .setDepth(t.SPOTLIGHT_DEPTH);

      // placedBags live inside balanceScale's rightTray — extracting
      // them via spotlight would detach them from the pan. The whole
      // balanceScale is already lifted, so they render lit.
      t.spotlight([this.balanceScale]);
      // Internal offset in showTooltip nudges (x +20, y -35), so the
      // tooltip container ends up at (980, 650) — well to the right of
      // the button and on the same y line.
      t.showTooltip('Confirm your answer', {
        x: 960,
        y: 685,
        arrow: 'left',
      });
    }
  }

  onTutorialAdvance() {
    this.advanceTutorial();
  }

  /** Make every weight bag draggable (used during tutorial steps so the
   *  student can interact with any bag, not just the highlighted one). */
  enableAllBagsForDrag() {
    this.draggables.forEach(bag => {
      if (!bag.input) {
        bag.setInteractive({ cursor: 'grab' });
        this.input.setDraggable(bag);
      }
    });
  }

  /** Resolve `done()` the moment ANY bag of the named kg lands on the
   *  pan. Ghosts double up the bags per kg, so either twin should
   *  satisfy the tutorial. Listens on each candidate's dragend and
   *  auto-detaches once matched. */
  watchForBagOnPan(kg, done) {
    const targets = this.draggables.filter(b => b.getData('kg') === kg);
    if (!targets.length) return done();
    let resolved = false;
    const handlers = new Map();
    targets.forEach(target => {
      const handler = () => {
        this.time.delayedCall(250, () => {
          if (resolved) return;
          if (targets.some(t => t && t.scene && t.getData('onPan'))) {
            resolved = true;
            targets.forEach(t => {
              const h = handlers.get(t);
              if (t && h) t.off('dragend', h);
            });
            done();
          }
        });
      };
      handlers.set(target, handler);
      target.on('dragend', handler);
    });
  }

  /**
   * Confirm-button payoff: confetti bursts on the right pan, then the
   * answer bag (matching the target weight) pops in at scale 0 and
   * cartoon-falls onto the floor.
   */
  playWeighReveal() {
    if (this.weighBtn) this.weighBtn.disableInteractive();
    if (this.tutorialHand) {
      this.tutorialHand.destroy();
      this.tutorialHand = null;
    }
    // Reset depths AND drop the overlay (no-op for post-tutorial calls
    // since the overlay is already hidden).
    if (this.tutorial) this.tutorial.hide();
    this.markProgressComplete(this.questionIndex);

    // Green glow behind the bag stack — confirms the correct answer
    // before the confetti/carry-bag payoff plays.
    this.spawnAnswerHighlight(true);

    const panPos = this.balanceScale.getRightTrayPos();

    // Confetti burst centred on the right pan.
    // Confetti centred on the right pan, nudged down so the particles
    // spread around the bags rather than the rim.
    const confetti = this.add
      .sprite(panPos.x, panPos.y + 50, 'balanceConfetti')
      .setDepth(130);
    confetti.play('balanceConfetti');
    confetti.once('animationcomplete', () => confetti.destroy());

    // The carryBag atlases are pre-laid-out on a full 1280x720 canvas.
    // Back layer plays behind the front so the bag has depth. Slowed
    // down a touch so the bag-into-bag drops have room to breathe.
    const slowMo = 0.6;
    const carryBack = this.add
      .sprite(640, 360, 'carryBagBackAnim')
      .setOrigin(0.5, 0.5)
      .setDepth(124);
    carryBack.play('carryBagBack');
    carryBack.anims.timeScale = slowMo;
    carryBack.once('animationcomplete', () => carryBack.destroy());

    const carryFront = this.add
      .sprite(640, 360, 'carryBagFrontAnim')
      .setOrigin(0.5, 0.5)
      .setDepth(125);
    carryFront.play('carryBagFront');
    carryFront.anims.timeScale = slowMo;
    carryFront.once('animationcomplete', () => {
      carryFront.destroy();
      this.liftAndExitTarget();
    });

    // Each placed bag rises STRAIGHT UP from the pan to a point above
    // the carry bag, then drops STRAIGHT DOWN into the opening. Depth
    // 124.5 lands between carryBack and carryFront so the front of the
    // bag covers the bag as it enters.
    const aboveCarry = { x: 924, y: 250 };
    const intoCarry = { x: 924, y: 440 };

    // Fade the green glow out as the bags start lifting off the pan.
    this.time.delayedCall(680, () => this.destroyAnswerHighlight(true));

    const dropOrder = [...this.placedBags].reverse();
    dropOrder.forEach((bag, i) => {
      this.time.delayedCall(700 + i * 500, () => {
        if (!bag.scene) return;
        this.detachFromTray(bag);
        bag.setData('stuck', false);
        bag.setDepth(124.5);
        const startX = bag.x;
        // Phase 1: straight UP to the top of the carry bag.
        this.tweens.add({
          targets: bag,
          x: aboveCarry.x,
          y: aboveCarry.y,
          duration: 450,
          ease: 'Sine.easeOut',
          onComplete: () => {
            if (!bag.scene) return;
            void startX;
            // Phase 2: straight DOWN into the carry bag opening.
            this.tweens.add({
              targets: bag,
              y: intoCarry.y,
              scale: 0.7,
              alpha: 0,
              duration: 400,
              ease: 'Quad.easeIn',
              onComplete: () => bag.destroy(),
            });
          },
        });
      });
    });
    this.placedBags = [];
  }

  /**
   * After the carry bag has left, lift the target weight off the pan
   * and slide it out to the left, then end the tutorial. Also relevels
   * the scale (no weight on either side).
   */
  liftAndExitTarget() {
    if (!this.targetSprite || !this.targetSprite.scene) {
      this.onAnswerComplete();
      return;
    }
    // Re-parent into the scene root so we can tween in world coords.
    if (this.targetSprite.parentContainer) {
      const parent = this.targetSprite.parentContainer;
      const wm = this.targetSprite.getWorldTransformMatrix();
      const wx = wm.tx;
      const wy = wm.ty;
      parent.remove(this.targetSprite, false);
      this.add.existing(this.targetSprite);
      this.targetSprite.x = wx;
      this.targetSprite.y = wy;
    }
    this.targetSprite.setDepth(50);

    // Re-level the scale (no weight on either pan once this is gone).
    this.balanceScale.setBalance(0, true);

    // Lift, then slide off-screen to the left.
    this.tweens.add({
      targets: this.targetSprite,
      y: this.targetSprite.y - 90,
      duration: 350,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: this.targetSprite,
          x: -200,
          duration: 700,
          ease: 'Sine.easeIn',
          onComplete: () => {
            this.targetSprite.destroy();
            this.targetSprite = null;
            this.onAnswerComplete();
          },
        });
      },
    });
  }

  /**
   * Called after the target weight has slid off — wrap the current
   * question and either advance to the next one or end the game.
   */
  onAnswerComplete() {
    if (this.questionIndex === 0) {
      // First time through: tear down the tutorial UI.
      this.finishTutorial();
    } else if (this.weighBtn) {
      this.weighBtn.destroy();
      this.weighBtn = null;
    }
    this.attempted += 1;
    this.questionIndex += 1;
    if (this.questionIndex >= QUESTIONS.length) {
      // All questions done (idx 5 = Q6 just wrapped) — launch the
      // end screen as an overlay scene on top of the current frame
      // and forward the run's stats so EndScene can render the
      // score line + hint count.
      // Tutorial (idx 0) never increments `score`, so the max
      // achievable score is QUESTIONS.length - 1. "Won" = every
      // non-tutorial question solved correctly.
      const scoringQuestions = QUESTIONS.length - 1;
      this.scene.launch('EndScene', {
        score: this.score,
        attempted: this.attempted,
        totalHints: this.totalHints,
        totalAttempts: this.totalAttempts,
        questionCount: scoringQuestions,
        won: this.score >= scoringQuestions,
      });
      return;
    }
    this.loadQuestion(this.questionIndex);
  }

  /**
   * Switches the gameplay state to the question at `idx`: clears the
   * shelf bags + ghosts, sets the new target weight, repopulates the
   * shelf, drops the new target onto the left pan.
   */
  loadQuestion(idx) {
    const q = QUESTIONS[idx];
    this.targetKg = q.targetKg;
    this.currentBagHomes = bagHomesFor(q.bags);
    this.attempts = 0;
    this.hideHintButton();
    this.hideHintPopup();

    // Clear out the previous shelf bags and ghosts.
    (this.draggables || []).forEach(b => b.scene && b.destroy());
    (this.bagGhosts || []).forEach(g => g.scene && g.destroy());
    this.destroyAnswerHighlight();
    this.draggables = [];
    this.bagGhosts = [];
    this.placedBags = [];

    // Spawn the new bags on the shelf (start invisible, then pop in).
    this.spawnDraggableBags(q.bags);
    this.draggables.forEach(bag => {
      this.tweens.add({
        targets: bag,
        alpha: 1,
        scale: bag.getData('targetScale') ?? 1,
        duration: 240,
        ease: 'Sine.easeOut',
      });
      bag.setInteractive({ cursor: 'grab' });
      this.input.setDraggable(bag);
    });

    // Once the main bags settle, peel a ghost duplicate out from
    // behind each — same slide-in effect as the tutorial entrance.
    this.time.delayedCall(280, () => this.spawnBagGhosts(0));

    // Drop the new target onto the left pan.
    this.targetSprite = this.add
      .image(LAYOUT.scale.pivotX, -120, `weight-block-${q.targetKg}kg`)
      .setOrigin(0.5, 1)
      .setDepth(0);
    this.time.delayedCall(280, () => {
      if (!this.targetSprite) return;
      const leftPanWorld = this.balanceScale.getLeftTrayPos();
      this.targetSprite.x = leftPanWorld.x;
      this.tweens.add({
        targets: this.targetSprite,
        y: leftPanWorld.y,
        duration: 480,
        ease: 'Quad.easeIn',
        onComplete: () => {
          if (!this.targetSprite) return;
          this.attachToLeftPan(this.targetSprite);
          this.balanceScale.setWeights(this.targetKg, 0);
        },
      });
    });
  }

  /**
   * Marks the nth progress dot complete: swap the orange circle for the
   * imported `greenLight` sprite at the same position.
   */
  markProgressComplete(idx) {
    const dot = this.progressDots && this.progressDots[idx];
    if (!dot || !this.progressBar) return;
    const light = this.add.image(dot.x, dot.y, 'greenLight').setOrigin(0.5, 0.5);
    // Insert into the same container, render order = where the dot is.
    const dotIdx = this.progressBar.list.indexOf(dot);
    this.progressBar.addAt(light, dotIdx);
    // Remove the original orange dot so only the green light shows.
    dot.destroy();
    this.progressDots[idx] = light;
  }

  finishTutorial() {
    this.input.off('pointerdown', this.onTutorialAdvance, this);
    this.tutorial.hide();
    if (this.weighBtn) {
      this.weighBtn.destroy();
      this.weighBtn = null;
    }
    // Open up the remaining (not-yet-destroyed) bags for free play.
    this.draggables.forEach(bag => {
      if (!bag.scene) return; // destroyed during the carry-bag reveal
      if (!bag.input) {
        bag.setInteractive({ cursor: 'grab' });
        this.input.setDraggable(bag);
      }
    });
  }

  // ==================== Static decoration ====================

  drawScale() {
    this.balanceScale = new BalanceScale(this, LAYOUT.scale.startX, LAYOUT.scale.pivotY)
      .setScale(LAYOUT.scale.scale)
      .setDepth(0);
  }

  drawProgress() {
    const x = LAYOUT.progress.x;
    const totalDots = LAYOUT.progress.dots;
    const bgW = 260;
    const bgH = 44;
    const dotR = 9;
    const cornerR = 8;
    const borderW = 4;
    const dotSpacing = (bgW - 50) / (totalDots - 1);
    const startX = -(bgW - 50) / 2;
    const orange = 0xee7a3d;
    const cream = 0xfff1e3;

    // The hanging sign: cream fill, thick orange border with a small
    // corner radius.
    const bg = this.add.graphics();
    bg.fillStyle(cream, 1);
    bg.fillRoundedRect(-bgW / 2, -bgH / 2, bgW, bgH, cornerR);
    bg.lineStyle(borderW, orange, 1);
    bg.strokeRoundedRect(-bgW / 2, -bgH / 2, bgW, bgH, cornerR);

    this.progressDots = [];
    for (let i = 0; i < totalDots; i++) {
      const dot = this.add
        .circle(startX + i * dotSpacing, 0, dotR, orange, 1)
        .setAlpha(0)
        .setScale(0.4);
      this.progressDots.push(dot);
    }
    this.progressBar = this.add
      .container(x, LAYOUT.progress.startY, [bg, ...this.progressDots])
      .setDepth(8);

    // Hanging cords drawn separately in world space so their top stays
    // pinned to the screen edge while the bar drops — drawn underneath
    // the bar so the bar's border overlaps the cord ends.
    this.progressCords = this.add.graphics().setDepth(7);
    this.progressCordsConfig = {
      orange,
      offsetX: bgW / 2 - 28,
      barTopOffset: bgH / 2,
    };
    this.redrawProgressCords();
  }

  redrawProgressCords() {
    if (!this.progressCords) return;
    const { orange, offsetX, barTopOffset } = this.progressCordsConfig;
    const barX = this.progressBar.x;
    const barTopY = this.progressBar.y - barTopOffset;

    this.progressCords.clear();
    // Only draw once the bar has entered the visible area, otherwise the
    // cord would render upward from y=0 into negative space (invisible
    // anyway, but clearer to skip).
    if (barTopY <= 0) return;

    this.progressCords.lineStyle(3, orange, 1);
    this.progressCords.beginPath();
    this.progressCords.moveTo(barX - offsetX, 0);
    this.progressCords.lineTo(barX - offsetX, barTopY);
    this.progressCords.moveTo(barX + offsetX, 0);
    this.progressCords.lineTo(barX + offsetX, barTopY);
    this.progressCords.strokePath();
  }

  drawTargetBlock(kg) {
    this.targetSprite = this.add
      .image(LAYOUT.scale.pivotX, -120, `weight-block-${kg}kg`)
      .setOrigin(0.5, 1)
      .setDepth(0)
      .setAlpha(0);
    this.targetKg = kg;
  }

  // ==================== Draggable bags ====================

  spawnDraggableBags(kgList) {
    const style = bagStyleFor(this.questionIndex);
    this.draggables = kgList.map(kg =>
      this.makeDraggableBag(kg, this.currentBagHomes[kg], style),
    );
  }

  /**
   * Returns a texture key for `style` + `kg`. The sack style has the
   * kg label baked into its PNG; all other styles use a neutral PNG
   * and the kg label is painted on top at runtime, the result being
   * cached as a new texture (`{baseKey}--{kg}kg`) so subsequent
   * spawns reuse it without redrawing.
   */
  bakeBagTexture(style, kg) {
    const cfg = BAG_STYLES[style];

    // Prefer a pre-baked per-kg PNG (`bag-card-purple-3kg`, etc.) when
    // the artist has imported one — keeps the `-right` / `-wrong`
    // highlight texture-swap working naturally.
    const preBakedKey = `${cfg.baseKey}-${kg}kg`;
    if (this.textures.exists(preBakedKey)) return preBakedKey;

    if (cfg.preBaked) {
      // Sack-style — fully pre-baked, but the requested kg isn't
      // imported. Fall back to the 5 kg variant if present.
      return this.textures.exists(`${cfg.baseKey}-5kg`)
        ? `${cfg.baseKey}-5kg`
        : cfg.baseKey;
    }

    // Single-dash naming so the runtime-baked texture aligns with the
    // imported `<baseKey>-<kg>kg-{right,wrong}` highlight variants —
    // the spawnAnswerHighlight lookup can find them by appending the
    // suffix to this key. The early-return above prevents any clash
    // with pre-baked PNGs of the same name.
    const newKey = `${cfg.baseKey}-${kg}kg`;
    if (this.textures.exists(newKey)) return newKey;
    if (!this.textures.exists(cfg.baseKey)) return cfg.baseKey;

    const src = this.textures.get(cfg.baseKey).getSourceImage();
    const w = src.width;
    const h = src.height;
    const { color, offsetY, fontSize } = cfg.label;

    const rt = this.add.renderTexture(0, 0, w, h).setVisible(false);
    // For a STRING texture key, Phaser draws at (x, y) as the TOP-LEFT
    // corner — so (0, 0) here fills the RT with the full bag image.
    // Passing (w/2, h/2) would offset the image and crop most of it.
    rt.draw(cfg.baseKey, 0, 0);

    const txt = this.make.text(
      {
        x: 0,
        y: 0,
        text: `${kg} kg`,
        style: {
          fontFamily: 'Athletics-Bold, Athletics-Regular, sans-serif',
          fontSize: `${fontSize}px`,
          color,
        },
      },
      false,
    );
    txt.setOrigin(0.5, 0.5);
    // For a Game Object with origin set, draw() positions the origin
    // at (x, y) — so this centres the kg label on the bag.
    rt.draw(txt, w / 2, h / 2 + offsetY);
    txt.destroy();

    rt.saveTexture(newKey);
    rt.destroy();
    return newKey;
  }

  makeDraggableBag(kg, home, style) {
    const key = this.bakeBagTexture(style, kg);
    const targetScale = bagTargetScale(style, kg);
    const sprite = this.add
      .image(home.x, home.y, key)
      .setOrigin(0.5, 0.5)
      .setDepth(2)
      .setAlpha(0)
      // Pre-state for the entrance / load-question fade-in tween: a
      // bit smaller than the bag's final per-kg display scale.
      .setScale(0.5 * targetScale);

    sprite.setData('kg', kg);
    sprite.setData('targetScale', targetScale);
    sprite.setData('home', { x: home.x, y: home.y });
    sprite.setData('onPan', false);

    sprite.on('dragstart', pointer => {
      this.detachFromTray(sprite);
      sprite.setData('stuck', false);
      sprite.setDepth(120);
      // Any leftover red glow from a prior wrong attempt should clear
      // as soon as the student starts re-arranging.
      this.destroyAnswerHighlight(true);
      // Phaser's dragX/dragY are computed at dragstart against the
      // sprite's THEN-current parent (the rightTray container for
      // pan bags). detachFromTray reparents the sprite into scene
      // root above, so the cached offset is stale and the bag would
      // teleport on the first drag tick. Capture a fresh world-space
      // pickup offset and drive the drag from pointer.worldX/Y.
      sprite.setData('grabDX', sprite.x - pointer.worldX);
      sprite.setData('grabDY', sprite.y - pointer.worldY);
    });

    sprite.on('drag', pointer => {
      sprite.x = pointer.worldX + sprite.getData('grabDX');
      sprite.y = pointer.worldY + sprite.getData('grabDY');
    });

    sprite.on('dragend', () => {
      if (this.isOverRightPan(sprite)) {
        const alreadyOnPan = sprite.getData('onPan');
        if (!alreadyOnPan && this.placedBags.length >= MAX_BAGS_ON_PAN) {
          this.returnHome(sprite);
        } else {
          this.snapToPan(sprite);
        }
      } else {
        this.returnHome(sprite);
      }
    });

    return sprite;
  }

  /**
   * Reparents `obj` to `targetContainer` while preserving its on-screen
   * position. `obj` must already have a parent (the world list counts).
   */
  reparentPreservingWorldPos(obj, targetContainer) {
    const wm = obj.getWorldTransformMatrix();
    const worldX = wm.tx;
    const worldY = wm.ty;

    // Detach from current parent
    if (obj.parentContainer) {
      obj.parentContainer.remove(obj, false);
    } else {
      this.children.remove(obj);
    }

    targetContainer.add(obj);
    // Convert world → target-container local.
    const localPoint = targetContainer
      .getWorldTransformMatrix()
      .applyInverse(worldX, worldY);
    obj.x = localPoint.x;
    obj.y = localPoint.y;
  }

  attachToLeftPan(obj) {
    this.reparentPreservingWorldPos(obj, this.balanceScale.leftTray);
    // Depth 0 sits inside the tray's band: tray-top (-10) < bag (0) < bowl (10).
    obj.setDepth(0);
    this.balanceScale.leftTray.sort('depth');
    // Belt-and-suspenders: pin the bag to be one slot below the bowl
    // so it always renders behind it, regardless of sort-tie behaviour.
    this.balanceScale.leftTray.moveBelow(obj, this.balanceScale.leftTraySprite);
  }

  attachToRightPan(obj, stackIndex) {
    void stackIndex;
    this.reparentPreservingWorldPos(obj, this.balanceScale.rightTray);
    obj.setDepth(0);
    this.balanceScale.rightTray.sort('depth');
    this.balanceScale.rightTray.moveBelow(obj, this.balanceScale.rightTraySprite);
  }

  detachFromTray(obj) {
    if (!obj.parentContainer) return;
    const wm = obj.getWorldTransformMatrix();
    obj.parentContainer.remove(obj, false);
    this.add.existing(obj);
    obj.x = wm.tx;
    obj.y = wm.ty;
  }

  isOverRightPan(sprite) {
    const pan = this.balanceScale.getRightTrayPos();
    const halfW = 140;
    const halfH = 80;
    return (
      sprite.x >= pan.x - halfW &&
      sprite.x <= pan.x + halfW &&
      sprite.y >= pan.y - halfH &&
      sprite.y <= pan.y + halfH
    );
  }

  snapToPan(sprite) {
    if (!sprite.getData('onPan')) {
      this.placedBags.push(sprite);
      sprite.setData('onPan', true);
    }
    const idx = this.placedBags.indexOf(sprite);
    const rightPanWorld = this.balanceScale.getRightTrayPos();
    const { dx, dy } = stackOffset(idx);
    this.tweens.add({
      targets: sprite,
      x: rightPanWorld.x + dx,
      y: rightPanWorld.y + dy,
      duration: 200,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        this.attachToRightPan(sprite, idx);
        sprite.setData('stuck', true);
        this.applyBalanceTilt();
      },
    });
    this.refreshConfirm();
  }

  returnHome(sprite) {
    if (sprite.getData('onPan')) {
      this.placedBags = this.placedBags.filter(s => s !== sprite);
      sprite.setData('onPan', false);
    }
    const home = sprite.getData('home');
    this.tweens.add({
      targets: sprite,
      x: home.x,
      y: home.y,
      duration: 250,
      ease: 'Back.easeOut',
    });
    sprite.setDepth(2);
    this.refreshConfirm();
    this.applyBalanceTilt();
  }

  applyBalanceTilt() {
    const rightSum = this.placedBags.reduce((a, s) => a + s.getData('kg'), 0);
    this.balanceScale.setWeights(this.targetKg, rightSum);
  }

  // ==================== Confirm + answer check ====================

  drawConfirmButton() {
    this.confirmBtn = new GameButton(this, 1130, 660, 'nextButton')
      .setHoverTexture('hover_nextButton')
      .disableEffects()
      .setVisible(false)
      .onClick(() => this.checkAnswer());
    this.confirmBtn.setDepth(15);
  }

  refreshConfirm() {
    // Built-in nextButton is hidden — we drive confirmation via the
    // separate "Let's Weigh!" button in non-tutorial questions.
    this.confirmBtn.setVisible(false);
    if (this.questionIndex === 0) return; // tutorial drives its own button
    const hasBags = this.placedBags.length > 0;
    if (hasBags) {
      this.ensureWeighButton();
      this.weighBtn.setVisible(true);
    } else if (this.weighBtn) {
      this.weighBtn.setVisible(false);
    }
  }

  /** Lazily creates the post-tutorial "Let's Weigh!" button. */
  ensureWeighButton() {
    if (this.weighBtn && this.weighBtn.scene) return;
    this.weighBtn = new GameButton(this, 640, 650, 'letsWeigh', {
      hoverScale: 1.08,
      pressScale: 0.92,
      tweenDuration: 120,
    })
      .onClick(() => {
        const sum = this.placedBags.reduce((a, s) => a + s.getData('kg'), 0);
        const correct = sum === this.targetKg;
        this.totalAttempts += 1;
        this.tweens.killTweensOf(this.weighBtn);
        this.tweens.add({
          targets: this.weighBtn,
          scaleX: 1,
          scaleY: 1,
          duration: 120,
          ease: 'Sine.easeOut',
          onComplete: () => {
            if (correct) {
              this.score += 1;
              this.weighBtn.setVisible(false);
              this.hideHintButton();
              this.playWeighReveal();
            } else {
              this.playWrongFeedback();
            }
          },
        });
      })
      .setDepth(15);
  }

  // ==================== Right/wrong highlight ====================

  /**
   * Highlight every bag resting on the right pan.
   *
   * Correct answers: swap each bag to its pre-rendered `-right`
   * variant if one exists; otherwise drop a green-tinted oversize
   * silhouette behind the bag as a programmatic stand-in.
   *
   * Wrong answers: texture-swap only. Bags without an imported
   * `-wrong` variant are left untouched — no tint fallback.
   */
  spawnAnswerHighlight(isCorrect) {
    this.destroyAnswerHighlight();

    if (!this.placedBags || this.placedBags.length === 0) return null;

    const suffix = isCorrect ? '-right' : '-wrong';
    this.highlightedBags = [];
    this.highlightOutlines = [];

    this.placedBags.forEach(bag => {
      const baseKey = bag.texture && bag.texture.key;
      if (!baseKey) return;

      const variantKey = `${baseKey}${suffix}`;
      if (this.textures.exists(variantKey)) {
        bag.setData('hlBaseKey', baseKey);
        bag.setTexture(variantKey);
        this.highlightedBags.push(bag);
        return;
      }

      if (!isCorrect) return; // wrong-answer path: no tint fallback

      // Correct-answer fallback only: green-tinted oversize silhouette
      // behind the bag, peeking out as a programmatic border.
      const frameName = bag.frame && bag.frame.name;
      const outline = this.add
        .image(bag.x, bag.y, baseKey, frameName)
        .setOrigin(bag.originX, bag.originY)
        .setScale(bag.scaleX * 1.1, bag.scaleY * 1.1)
        .setTint(0x1fc24c)
        .setDepth(-5);
      this.balanceScale.rightTray.add(outline);
      this.highlightOutlines.push(outline);
    });

    this.balanceScale.rightTray.sort('depth');
    return this.highlightedBags;
  }

  /** Restore originals: texture-swapped bags revert, outline copies
   *  are destroyed. */
  destroyAnswerHighlight() {
    const bags = this.highlightedBags;
    if (bags && bags.length) {
      bags.forEach(bag => {
        if (!bag.scene) return;
        const baseKey = bag.getData('hlBaseKey');
        if (baseKey && this.textures.exists(baseKey)) bag.setTexture(baseKey);
        bag.setData('hlBaseKey', null);
      });
    }
    this.highlightedBags = null;

    const outlines = this.highlightOutlines;
    if (outlines && outlines.length) {
      outlines.forEach(o => o.scene && o.destroy());
    }
    this.highlightOutlines = null;
  }

  // ==================== Wrong-answer feedback + hint ====================

  /**
   * Plays the on-wrong feedback: shakes the weigh button and placed bags
   * for a quick "no" cue, increments the per-question attempt counter,
   * and pops the hint button in (so the student can ask for help).
   */
  playWrongFeedback() {
    this.attempts = (this.attempts || 0) + 1;

    if (this.weighBtn && this.weighBtn.scene) {
      const baseX = this.weighBtn.x;
      this.tweens.killTweensOf(this.weighBtn);
      this.tweens.add({
        targets: this.weighBtn,
        x: baseX + 14,
        duration: 55,
        yoyo: true,
        repeat: 4,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          if (this.weighBtn && this.weighBtn.scene) this.weighBtn.x = baseX;
        },
      });
    }

    // Red glow behind the bag stack to mark the wrong answer.
    this.spawnAnswerHighlight(false);

    // Angle wobble on each placed bag. The per-frame position glue in
    // update() forbids any x/y wobble, so we rock the bag on its local
    // angle instead — reads as a clear "shake-no" cue without fighting
    // the stack layout. (No tint pulse — the `-wrong` texture swap on
    // bags that have a variant carries the red signal.)
    this.placedBags.forEach((bag, i) => {
      if (!bag.scene) return;
      this.time.delayedCall(i * 30, () => {
        if (!bag.scene) return;
        this.wobbleAngle(bag, 6);
      });
    });

    // Fade the red glow after the wobble settles so the next attempt
    // starts on a clean pan.
    this.time.delayedCall(900, () => this.destroyAnswerHighlight(true));

    this.popHintButton();
  }

  /**
   * Rock `target` back-and-forth on its local angle for a quick "no"
   * cue. Resets to the base angle on completion. Position is left
   * alone — the per-frame stack glue in update() would otherwise win.
   */
  wobbleAngle(target, amplitude = 6) {
    if (!target || !target.scene) return;
    const baseAngle = target.angle || 0;
    this.tweens.killTweensOf(target);
    this.tweens.add({
      targets: target,
      angle: baseAngle + amplitude,
      duration: 60,
      yoyo: true,
      repeat: 5,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        if (!target.scene) return;
        target.angle = baseAngle;
      },
    });
  }

  /**
   * Hint button — animated lightbulb spritesheet (cardistry pattern).
   * Lives hidden + non-interactive until `popHintButton` plays the
   * pop-in animation. Click opens the hint modal. Depth 21 sits ABOVE
   * the hint-modal dim overlay (20) so the lightbulb stays visible
   * while the modal is up.
   */
  drawHintButton() {
    this.hintButton = this.add
      .sprite(80, 650, 'hintFinalAnim')
      .setScale(0.8)
      .setDepth(23)
      .setVisible(false)
      .on('pointerup', () => this.showHintPopup());
    this.hintButton.disableInteractive();
  }

  /**
   * Plays the pop-in animation and queues the breathe loop once it
   * finishes (cardistry's `delayedCall(1500, ... play('breathe'))`).
   */
  popHintButton() {
    if (!this.hintButton) this.drawHintButton();
    if (this.hintButton.getData('shown')) return;
    this.hintButton.setData('shown', true);
    this.hintButton.setVisible(true);
    this.hintButton.setInteractive({ cursor: 'pointer' });
    this.hintButton.play('hintPop');
    this.time.delayedCall(1500, () => {
      if (
        this.hintButton &&
        this.hintButton.scene &&
        this.hintButton.getData('shown') &&
        !this.hint?.visible
      ) {
        this.hintButton.play('hintBreathe');
      }
    });
  }

  /**
   * Plays the pop-out animation and hides the button on complete.
   * Matches cardistry: once dismissed (or correct answer), the button
   * collapses back to invisible until the next wrong attempt.
   */
  hideHintButton() {
    if (!this.hintButton || !this.hintButton.getData('shown')) return;
    this.hintButton.setData('shown', false);
    this.hintButton.disableInteractive();
    this.hintButton.play('hintPopOut');
    this.hintButton.once('animationcomplete-hintPopOut', () => {
      if (this.hintButton && this.hintButton.scene && !this.hintButton.getData('shown')) {
        this.hintButton.setVisible(false);
      }
    });
  }

  /**
   * Lazily creates the hint modal — adopted from cardistry's layout:
   *   - the hint card sits at (240, 575) at scale 0.8, so its
   *     bottom-left corner falls right next to the lightbulb at
   *     (80, 650) (the lightbulb visually anchors the card)
   *   - the close X sits just outside the card's top-right corner
   *   - a full-screen dim `overlay` covers the rest of the scene
   *
   * Depth band: overlay (20) < hint (21) < closeHintButton (22) <
   * hintButton (23). So everything else in the scene is dimmed, the
   * close button sits above the hint, and the lightbulb sits ON TOP
   * of the hint (overlapping its bottom-left corner).
   *
   * Page indicator ("Hint: 1/2" vs "2/2") is baked into the artwork,
   * so there's no Next button — `hint_1` shows on attempt 1,
   * `hint_2` from attempt 2 onwards.
   */
  ensureHintSurface() {
    if (this.hint) return;

    // Dim backdrop — full-screen, also catches clicks so they don't
    // leak through to the bags/scale while the hint is open.
    this.hintOverlay = this.add
      .image(0, 0, 'overlay')
      .setOrigin(0, 0)
      .setDisplaySize(1280, 720)
      .setAlpha(0)
      .setDepth(20)
      .setVisible(false)
      .setInteractive();

    // The hint card — cardistry-style position and scale. (240, 575)
    // with origin (0.5, 0.5) places its bottom-left ~ (64, 668), which
    // tucks the lightbulb at (80, 650) into the card's bottom-left.
    this.hint = this.add
      .sprite(240, 575, 'hint_1')
      .setOrigin(0.5, 0.5)
      .setScale(0.8)
      .setVisible(false)
      .setDepth(21);

    // Close (X) — sits just outside the card's top-right corner.
    // Smaller than its native size so it reads as a tiny tap target
    // rather than competing with the hint art.
    this.closeHintButton = this.add
      .sprite(430, 482, 'hintCloseButton')
      .setOrigin(0.5, 0.5)
      .setScale(0.5)
      .setVisible(false)
      .setDepth(22)
      .setInteractive({ cursor: 'pointer' })
      .on('pointerup', () => this.hideHintPopup());
  }

  /**
   * Opens the hint modal. Shows `hint_1` on the first attempt and
   * `hint_2` from the second onwards (auto-paging, cardistry style).
   * Switches the lightbulb to its idle animation so it stops
   * breathing while the modal is up.
   */
  showHintPopup() {
    this.ensureHintSurface();
    this.totalHints += 1;

    (this.draggables || []).forEach(bag => {
      if (bag.scene && bag.input) bag.disableInteractive();
    });

    this.hint.setTexture(this.attempts >= 2 ? 'hint_2' : 'hint_1');

    // Fade backdrop + hint + close in (no scale tween — the card sits
    // at its fixed cardistry scale of 0.8).
    this.tweens.killTweensOf([this.hint, this.closeHintButton, this.hintOverlay]);
    this.hintOverlay.setVisible(true).setAlpha(0);
    this.hint.setVisible(true).setAlpha(0);
    this.closeHintButton.setVisible(true).setAlpha(0);
    this.tweens.add({
      targets: this.hintOverlay,
      alpha: 0.75,
      duration: 180,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: this.hint,
      alpha: 1,
      duration: 180,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: this.closeHintButton,
      alpha: 1,
      duration: 180,
      delay: 80,
      ease: 'Sine.easeOut',
    });

    // Pause the breathe loop while the modal is open.
    if (this.hintButton && this.hintButton.scene && this.hintButton.getData('shown')) {
      this.hintButton.play('hintIdle');
    }
  }

  /**
   * Closes the hint modal AND pops the lightbulb out. Cardistry's
   * close handler does both — once a hint has been read, the student
   * must trigger another wrong attempt before the button reappears.
   */
  hideHintPopup() {
    if (!this.hint || !this.hint.visible) return;
    this.tweens.killTweensOf([this.hint, this.closeHintButton, this.hintOverlay]);
    this.tweens.add({
      targets: this.hintOverlay,
      alpha: 0,
      duration: 180,
      ease: 'Sine.easeIn',
      onComplete: () => this.hintOverlay.setVisible(false),
    });
    this.tweens.add({
      targets: this.hint,
      alpha: 0,
      duration: 180,
      ease: 'Sine.easeIn',
      onComplete: () => this.hint.setVisible(false),
    });
    this.tweens.add({
      targets: this.closeHintButton,
      alpha: 0,
      duration: 180,
      ease: 'Sine.easeIn',
      onComplete: () => this.closeHintButton.setVisible(false),
    });

    this.hideHintButton();

    // Re-enable bag drag. `disableInteractive()` keeps bag.input
    // around with enabled=false, so the previous `!bag.input` guard
    // never fired — flip enabled directly, or rebuild input if it's
    // truly gone (e.g. after a placedBag's `disableInteractive()`
    // in playWeighReveal).
    (this.draggables || []).forEach(bag => {
      if (!bag.scene) return;
      if (bag.input) {
        bag.input.enabled = true;
      } else {
        bag.setInteractive({ cursor: 'grab' });
        this.input.setDraggable(bag);
      }
    });
  }

  checkAnswer() {
    const sum = this.placedBags.reduce((a, s) => a + s.getData('kg'), 0);
    const correct = sum === this.targetKg;
    if (correct) {
      this.placedBags.forEach(s => s.disableInteractive());
      this.confirmBtn.setVisible(false);
      this.playConfetti();
    }
  }
}
